import { type ReactElement, useCallback, useState } from 'react';
import {
  DesignGuideButton,
  EditorHintBar,
  EditorToolbar,
  editorHintOf,
  FaceSwitcher,
  type FlatFitMode,
  hasAnyLayer,
  siblingPosition,
} from '@/features/design-editor';
import { storeProductPath } from '@/shared/config';
import { DesignCanvas } from '@/widgets/design-canvas';
import type { CustomizeEditorReady } from '../model/use-customize-editor';
import { FitModeToggle } from './fit-mode-toggle';
import { ResetConfirmDialog } from './reset-confirm-dialog';
import { ToolRail } from './tool-rail';

interface EditorStageProps {
  readonly editor: CustomizeEditorReady;
  /** 텍스트를 추가하거나 캔버스에서 더블클릭했을 때 — 오른쪽 패널이 그 텍스트의 내용 칸에 포커스를 옮긴다. */
  readonly onEditText: (nodeId: string) => void;
}

/**
 * 왼쪽 스테이지 — 맨 위 [디자인하는 방법] 링크, 그 아래 얇은 선으로 두른 각진 작업판.
 * 작업판은 위 가장자리 도구 줄·안내 한 줄, 왼쪽 도구 칸(이미지·텍스트·상품 변경), 연회색 캔버스(목업 위 바로 편집)로 나뉜다.
 * 인쇄 면 알약은 캔버스 아래 가운데에, 보기 토글은 오른쪽 위에 떠 있다.
 * 좁은 화면에서는 도구 칸이 캔버스 아래 가로 줄이 된다.
 */
export const EditorStage = ({ editor, onEditText }: EditorStageProps): ReactElement => {
  const { store, definition, selected, selectedArea, actions } = editor;
  const [confirmingReset, setConfirmingReset] = useState(false);
  const closeReset = useCallback(() => setConfirmingReset(false), []);
  // 보기 모드 — 처음엔 전체 보기, [영역 확대]나 휠로 자유 확대·이동. 화면 설정이라 문서에 넣지 않는다.
  const [fitMode, setFitMode] = useState<FlatFitMode>('photo');
  const position =
    store.selectedNodeId === null ? null : siblingPosition(store.document, store.selectedNodeId);
  const box = selected !== null && selected.type !== 'group' ? selected : null;

  return (
    <section aria-label="디자인 스테이지" className="flex flex-col lg:min-h-0 lg:pr-8 xl:pr-10">
      <div className="flex items-center justify-end gap-4 border-b border-foreground/10 pb-2">
        <DesignGuideButton />
      </div>

      <div className="mt-4 flex h-[64vh] min-h-[480px] flex-col border border-foreground/10 lg:h-auto lg:min-h-0 lg:flex-1">
        <EditorToolbar
          canReset={editor.canReset}
          canUndo={store.past.length > 0}
          canRedo={store.future.length > 0}
          hasSelection={selected !== null}
          canAlign={box !== null && selectedArea !== null}
          canFlip={box?.type === 'image'}
          canBringForward={position !== null && position.index < position.count - 1}
          canSendBackward={position !== null && position.index > 0}
          onReset={() => setConfirmingReset(true)}
          onUndo={store.undo}
          onRedo={store.redo}
          onDelete={store.removeSelected}
          onBringForward={() => store.reorderSelected('up')}
          onSendBackward={() => store.reorderSelected('down')}
          onFlip={store.flipSelected}
          onAlign={(alignment) => {
            if (selectedArea !== null) store.alignSelected(alignment, selectedArea);
          }}
        />
        <EditorHintBar
          message={editorHintOf(selected?.type ?? null, hasAnyLayer(store.document))}
        />

        <div className="flex min-h-0 flex-1 flex-col-reverse lg:flex-row">
          <ToolRail
            changeProductHref={storeProductPath(editor.product.id)}
            imageDisabledReason={editor.imageDisabledReason}
            textDisabledReason={editor.textDisabledReason}
            guestKey={editor.guestKey}
            onImageUploaded={actions.addImage}
            onAddText={() => {
              const nodeId = actions.addText();
              if (nodeId !== null) onEditText(nodeId);
            }}
          />
          <div className="relative min-h-0 flex-1 bg-muted/70">
            <div className="absolute inset-0 p-3 sm:p-6 lg:p-8">
              <DesignCanvas
                definition={definition}
                materials={editor.materials}
                guide={editor.guide}
                createContext={editor.createContext}
                measure={editor.measure}
                onBackdropError={actions.refreshBackdrop}
                warped={editor.warped}
                onWarpedHit={actions.warpedHit}
                onEditText={onEditText}
                onChangeNode={actions.changeNode}
                onNudge={actions.nudgeSelected}
                onDeleteSelected={store.removeSelected}
                fitMode={fitMode}
                onFitModeChange={setFitMode}
              />
            </div>
            <FitModeToggle mode={fitMode} onChange={setFitMode} />
            {/* 인쇄 면 전환 — 캔버스 아래 가운데에 떠 있어 어느 면을 보고 있는지 바로 보인다. */}
            <div className="pointer-events-none absolute inset-x-0 bottom-3 flex justify-center">
              <div className="pointer-events-auto">
                <FaceSwitcher
                  faces={editor.faceOptions}
                  value={store.faceKey}
                  onChange={actions.changeFace}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <ResetConfirmDialog
        open={confirmingReset}
        onCancel={closeReset}
        onConfirm={() => {
          store.reset();
          closeReset();
        }}
      />
    </section>
  );
};
